import type { Authorization } from '@hitz/sdk'
import { hitz } from './index'
import { LEVEL_IDS } from './release.js'
import { createCheckpoint, type RoundStatus } from './telemetry'
import { Session, type PuttRecord } from '../core/engine'
import { roundPar, totalStrokes, type HoleScore } from '../core/rules'

/** Local analytics must never break play (docs/ARCHITECTURE.md, resilience): a refused event is logged, not thrown. */
const track = (name: string, data: Record<string, unknown>) => { try { hitz.track(name, data) } catch (e) { console.warn('[tacada-limpa] analytics event refused', name, e) } }

export interface RoundResult { levelId: string; score: number; won: boolean; strokes: number; par: number; cards: HoleScore[] }

/**
 * One Hitz attempt of Tacada Limpa, independent of the engine: ask the host → play only once authorized → `start` with the
 * initial checkpoint → one `putt` action per putt (fromX = angle 0..999, toX = strength 1..1000, elapsedMs) → terminal
 * checkpoint + outcome when the third hole ends. The engine calls `step(dtMs)` every frame and forwards putts to `putt()`.
 */
export class TacadaLimpaHitzAdapter {
  private finished: RoundResult | null = null

  private constructor(readonly auth: Authorization, readonly session: Session) {}

  /** Requests the attempt and starts it. Rejects when the host refuses (reasonCode) or the authorization is incompatible. */
  static async open(levelId: string): Promise<TacadaLimpaHitzAdapter> {
    if (!LEVEL_IDS.includes(levelId)) throw new Error(`level "${levelId}" is not one of ${LEVEL_IDS.join(', ')}`)
    const auth = await hitz.requestAttempt(levelId)
    const round = new TacadaLimpaHitzAdapter(auth, new Session(auth.levelId, auth.seed))
    hitz.start(round.checkpoint('playing'))
    track('level_started', { level: Number(levelId) })
    return round
  }

  get levelId() { return this.auth.levelId }
  get elapsedMs() { return Math.round(this.session.elapsedMs) }
  /** Set once the outcome was sent. */
  get result(): RoundResult | null { return this.finished }

  /** Advances match time (stops while paused: the engine stops calling it). */
  step(dtMs: number) { this.session.step(dtMs) }

  /** The player putts now. Returns the putt (the view animates its path) or null when the ball is still rolling. */
  putt(angle: number, strength: number): PuttRecord | null {
    if (this.finished) return null
    const a = ((Math.round(angle) % 1000) + 1000) % 1000, s = Math.max(1, Math.min(1000, Math.round(strength)))
    const hole = this.session.state.hole
    const rec = this.session.putt(a, s, true)
    if (!rec) return null
    hitz.action('putt', { fromX: a, toX: s }, rec.atMs)
    if (this.session.state.hole !== hole) {
      const card = this.session.state.cards[hole]
      track('hole_finished', { hole: hole + 1, strokes: card.strokes, par: card.par, points: card.points })
    }
    if (this.session.over) this.finish()
    return rec
  }

  /** Smoke hook: a reasonable putt straight at the cup. */
  puttTowardCup() {
    const { ball } = this.session, cup = this.session.hole.course.cup
    const angle = Math.round((Math.atan2(cup.y - ball.y, cup.x - ball.x) / (2 * Math.PI)) * 1000)
    return this.putt(angle, 520)
  }

  private checkpoint(status: RoundStatus) {
    return createCheckpoint(this.session, status, this.auth.levelId, this.auth.seed)
  }

  private finish() {
    const s = this.session, won = s.won
    if (won) hitz.complete(this.checkpoint('won')); else hitz.fail(this.checkpoint('lost'))
    track(won ? 'level_completed' : 'level_failed', { level: Number(this.levelId), score: s.score, durationMs: this.elapsedMs })
    this.finished = { levelId: this.levelId, score: s.score, won, strokes: totalStrokes(s.state), par: roundPar(s.round), cards: s.state.cards }
  }
}

/** Text for the screen when the host refuses or cannot start the attempt. */
export const startErrorText = (err: unknown) =>
  `Não foi possível começar: ${err && typeof err === 'object' && 'reasonCode' in err ? (err as { reasonCode: string }).reasonCode : (err as Error).message}`
